import React, { useState, useEffect, useCallback } from "react";
import { teksAPI } from "../../services/api";

const CATEGORIES = ["Readiness", "Supporting"];

const emptyDraft = () => ({
  teks_code: "",
  description: "",
  category: "Readiness",
  cba_item_weight: "",
});

function parsePasteText(text) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split("\t");
      return {
        teks_code: (parts[0] || "").trim(),
        description: (parts[1] || "").trim(),
        category: (parts[2] || "Readiness").trim() || "Readiness",
        cba_item_weight:
          parts[3] && parts[3].trim() !== ""
            ? Number(parts[3].trim()) || null
            : null,
      };
    })
    .filter((r) => r.teks_code && r.description);
}

function TeksTab({ courseId, onRefreshKpis }) {
  const [teks, setTeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  // Add form state
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState(emptyDraft());

  // Paste import state
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pastePreview, setPastePreview] = useState([]);
  const [pasteError, setPasteError] = useState("");
  const [importing, setImporting] = useState(false);

  // Delete confirm state
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const loadTeks = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError("");
    try {
      const res = await teksAPI.getAll(courseId);
      setTeks(res.data || []);
    } catch (err) {
      setError("Failed to load TEKS standards.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadTeks();
  }, [loadTeks]);

  // ---- Inline edit ----
  const startEdit = (row) => {
    setDeleteConfirmId(null);
    setAddOpen(false);
    setEditingId(row.id);
    setEditDraft({
      teks_code: row.teks_code || "",
      description: row.description || "",
      category: row.category || "Readiness",
      cba_item_weight: row.cba_item_weight != null ? String(row.cba_item_weight) : "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyDraft());
  };

  const saveEdit = async () => {
    if (!editDraft.teks_code.trim() || !editDraft.description.trim()) {
      alert("TEKS code and description are required.");
      return;
    }
    setSaving(true);
    try {
      await teksAPI.update(editingId, {
        teks_code: editDraft.teks_code.trim(),
        description: editDraft.description.trim(),
        category: editDraft.category,
        cba_item_weight:
          editDraft.cba_item_weight.trim() !== ""
            ? Number(editDraft.cba_item_weight)
            : null,
      });
      setEditingId(null);
      setEditDraft(emptyDraft());
      await loadTeks();
    } catch (err) {
      alert("Failed to save: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ---- Add ----
  const openAdd = () => {
    setEditingId(null);
    setDeleteConfirmId(null);
    setPasteOpen(false);
    setAddDraft(emptyDraft());
    setAddOpen(true);
  };

  const handleAdd = async () => {
    if (!addDraft.teks_code.trim() || !addDraft.description.trim()) {
      alert("TEKS code and description are required.");
      return;
    }
    setSaving(true);
    try {
      await teksAPI.create({
        course_id: courseId,
        teks_code: addDraft.teks_code.trim(),
        description: addDraft.description.trim(),
        category: addDraft.category,
        cba_item_weight:
          addDraft.cba_item_weight.trim() !== ""
            ? Number(addDraft.cba_item_weight)
            : null,
      });
      setAddOpen(false);
      setAddDraft(emptyDraft());
      await loadTeks();
      if (onRefreshKpis) onRefreshKpis();
    } catch (err) {
      alert("Failed to add TEKS: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete ----
  const handleDelete = async (id) => {
    setSaving(true);
    setDeleteError("");
    try {
      await teksAPI.delete(id);
      setDeleteConfirmId(null);
      await loadTeks();
      if (onRefreshKpis) onRefreshKpis();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to delete.";
      setDeleteError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ---- Bulk paste ----
  const openPaste = () => {
    setAddOpen(false);
    setEditingId(null);
    setDeleteConfirmId(null);
    setPasteText("");
    setPastePreview([]);
    setPasteError("");
    setPasteOpen(true);
  };

  const handlePasteChange = (text) => {
    setPasteText(text);
    const parsed = parsePasteText(text);
    setPastePreview(parsed);
    if (text.trim() && parsed.length === 0) {
      setPasteError(
        "No valid rows found. Expected: TEKS Code[tab]Description[tab]Category[tab]Weight"
      );
    } else {
      setPasteError("");
    }
  };

  const handleImport = async () => {
    if (pastePreview.length === 0) return;
    setImporting(true);
    setPasteError("");
    try {
      await teksAPI.bulkCreate({ course_id: courseId, standards: pastePreview });
      setPasteOpen(false);
      setPasteText("");
      setPastePreview([]);
      await loadTeks();
      if (onRefreshKpis) onRefreshKpis();
    } catch (err) {
      setPasteError(
        "Import failed: " + (err.response?.data?.error || err.message)
      );
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <p className="curriculum-loading">Loading TEKS standards...</p>;
  if (error) return <p className="curriculum-error">{error}</p>;

  return (
    <div className="curriculum-teks-tab">
      {/* Toolbar */}
      <div className="curriculum-tab-toolbar">
        <button className="primary-btn" onClick={openAdd}>
          + Add TEKS
        </button>
        <button className="secondary-btn" onClick={openPaste}>
          Paste Import
        </button>
        <span className="curriculum-count">{teks.length} standard{teks.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Add form */}
      {addOpen && (
        <div className="curriculum-add-form panel-card">
          <h4>Add TEKS Standard</h4>
          <div className="curriculum-form-row">
            <label>
              TEKS Code *
              <input
                value={addDraft.teks_code}
                onChange={(e) =>
                  setAddDraft({ ...addDraft, teks_code: e.target.value })
                }
                placeholder="e.g. §113.41(c)(1)(A)"
              />
            </label>
            <label>
              Category
              <select
                value={addDraft.category}
                onChange={(e) =>
                  setAddDraft({ ...addDraft, category: e.target.value })
                }
              >
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <label>
              CBA Weight
              <input
                type="number"
                min="0"
                max="100"
                value={addDraft.cba_item_weight}
                onChange={(e) =>
                  setAddDraft({ ...addDraft, cba_item_weight: e.target.value })
                }
                placeholder="optional"
              />
            </label>
          </div>
          <div className="curriculum-form-row">
            <label className="curriculum-full-width">
              Description *
              <textarea
                value={addDraft.description}
                onChange={(e) =>
                  setAddDraft({ ...addDraft, description: e.target.value })
                }
                rows={3}
                placeholder="Full TEKS description text..."
              />
            </label>
          </div>
          <div className="curriculum-form-actions">
            <button
              className="primary-btn"
              onClick={handleAdd}
              disabled={saving}
            >
              {saving ? "Saving..." : "Add Standard"}
            </button>
            <button
              className="secondary-btn"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Paste import */}
      {pasteOpen && (
        <div className="curriculum-paste-form panel-card">
          <h4>Bulk Paste Import</h4>
          <p className="curriculum-hint">
            Paste tab-separated rows:{" "}
            <code>TEKS Code[tab]Description[tab]Category[tab]CBA Weight</code>.
            Category and CBA Weight are optional.
          </p>
          <textarea
            className="curriculum-paste-area"
            rows={8}
            value={pasteText}
            onChange={(e) => handlePasteChange(e.target.value)}
            placeholder={"§113.41(c)(1)(A)\tThe student analyzes...\tReadiness\t4"}
          />
          {pasteError && <p className="curriculum-error">{pasteError}</p>}
          {pastePreview.length > 0 && (
            <div className="curriculum-paste-preview">
              <p>{pastePreview.length} row{pastePreview.length !== 1 ? "s" : ""} ready to import:</p>
              <table className="curriculum-table curriculum-table-sm">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {pastePreview.slice(0, 8).map((row, i) => (
                    <tr key={i}>
                      <td>{row.teks_code}</td>
                      <td>
                        {row.description.length > 80
                          ? row.description.slice(0, 80) + "…"
                          : row.description}
                      </td>
                      <td>{row.category}</td>
                      <td>{row.cba_item_weight ?? "—"}</td>
                    </tr>
                  ))}
                  {pastePreview.length > 8 && (
                    <tr>
                      <td
                        colSpan={4}
                        style={{ textAlign: "center", color: "#666", fontStyle: "italic" }}
                      >
                        …and {pastePreview.length - 8} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="curriculum-form-actions">
            <button
              className="primary-btn"
              onClick={handleImport}
              disabled={importing || pastePreview.length === 0}
            >
              {importing
                ? "Importing…"
                : `Import ${pastePreview.length} Standard${pastePreview.length !== 1 ? "s" : ""}`}
            </button>
            <button
              className="secondary-btn"
              onClick={() => {
                setPasteOpen(false);
                setPasteText("");
                setPastePreview([]);
                setPasteError("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete error banner */}
      {deleteError && (
        <p className="curriculum-error" style={{ marginBottom: "0.5rem" }}>
          {deleteError}
          <button
            style={{ marginLeft: "0.5rem", fontSize: "0.75rem" }}
            className="secondary-btn curriculum-btn-sm"
            onClick={() => setDeleteError("")}
          >
            Dismiss
          </button>
        </p>
      )}

      {/* Table */}
      {teks.length === 0 ? (
        <p className="curriculum-empty">
          No TEKS standards yet. Use <strong>+ Add TEKS</strong> or{" "}
          <strong>Paste Import</strong> to add standards.
        </p>
      ) : (
        <table className="curriculum-table">
          <thead>
            <tr>
              <th>TEKS Code</th>
              <th>Description</th>
              <th>Category</th>
              <th>CBA Weight</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {teks.map((row) => {
              if (editingId === row.id) {
                return (
                  <tr key={row.id} className="curriculum-editing-row">
                    <td>
                      <input
                        value={editDraft.teks_code}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, teks_code: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <textarea
                        value={editDraft.description}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, description: e.target.value })
                        }
                        rows={2}
                      />
                    </td>
                    <td>
                      <select
                        value={editDraft.category}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, category: e.target.value })
                        }
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={editDraft.cba_item_weight}
                        onChange={(e) =>
                          setEditDraft({
                            ...editDraft,
                            cba_item_weight: e.target.value,
                          })
                        }
                        style={{ width: "60px" }}
                      />
                    </td>
                    <td className="curriculum-row-actions">
                      <button
                        className="primary-btn curriculum-btn-sm"
                        onClick={saveEdit}
                        disabled={saving}
                      >
                        {saving ? "…" : "Save"}
                      </button>
                      <button
                        className="secondary-btn curriculum-btn-sm"
                        onClick={cancelEdit}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                );
              }

              if (deleteConfirmId === row.id) {
                return (
                  <tr key={row.id} className="curriculum-delete-row">
                    <td colSpan={5}>
                      <span>
                        Delete <strong>{row.teks_code}</strong>? This will
                        affect lessons using this standard.
                      </span>
                      <button
                        className="danger-btn curriculum-btn-sm"
                        onClick={() => handleDelete(row.id)}
                        disabled={saving}
                        style={{ marginLeft: "0.75rem" }}
                      >
                        {saving ? "Deleting…" : "Yes, Delete"}
                      </button>
                      <button
                        className="secondary-btn curriculum-btn-sm"
                        onClick={() => {
                          setDeleteConfirmId(null);
                          setDeleteError("");
                        }}
                        style={{ marginLeft: "0.375rem" }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={row.id}>
                  <td className="curriculum-code-cell">{row.teks_code}</td>
                  <td className="curriculum-desc-cell">{row.description}</td>
                  <td>
                    <span
                      className={`curriculum-badge curriculum-badge-${String(
                        row.category || ""
                      ).toLowerCase()}`}
                    >
                      {row.category || "—"}
                    </span>
                  </td>
                  <td>{row.cba_item_weight != null ? row.cba_item_weight : "—"}</td>
                  <td className="curriculum-row-actions">
                    <button
                      className="secondary-btn curriculum-btn-sm"
                      onClick={() => startEdit(row)}
                    >
                      Edit
                    </button>
                    <button
                      className="danger-btn curriculum-btn-sm"
                      onClick={() => {
                        setDeleteError("");
                        setEditingId(null);
                        setDeleteConfirmId(row.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default TeksTab;
